import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import styled from 'styled-components';
import {
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  SwipeableDrawer,
  Typography,
} from '@material-ui/core';
import { Menu as MenuIcon } from '@material-ui/icons';
import Avatar from '../../UI/Avatar/Avatar';
import ButtonWithTooltip from '../../UI/Buttons/ButtonWithTooltip';
import useScreen from '../../../hooks/useScreen';
import { homeRoutes, appRoutes } from '../../../config';

import Logo from '../../Logo/Logo';
import NavItems from '../NavItems/NavItems';

const FixedWrapper = styled(AppBar)`
  width: 100%;
`;

const Wrapper = styled(Toolbar)`
  display: flex;
  height: 100%;
  justify-content: space-between;
`;

const StyledTypographyMobileHeader = styled(Typography)`
  font-weight: 600;
`;

const StyledPremiumWrapper = styled.div`
  ${({ premiumUser }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;

    ${
      !premiumUser &&
      `
      min-width: 160px;
    `
    }
  `}
`;

const StyledAvatarMobileWrapper = styled.div`
  ${({ theme: { colors } }) => `
    flex-basis: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    background-color: ${colors.darkGray};
    padding-top: 30px;
  `}
`;

// TODO: handle this better
const renderMobileHeader = (location, formatMessage) => {
  const { pathname } = location;
  // If we are in app
  if (pathname.includes('/app/')) {
    // after /app in url will be route that we are at
    const splitedPathname = pathname?.split('/app/')[1].split('/')[0];
    switch (splitedPathname) {
      case 'dictionary':
        return formatMessage({
          id: 'NAVBAR.DICTIONARY',
          defaultMessage: '',
        });
      case 'dashboard':
        return formatMessage({
          id: 'NAVBAR.DASHBOARD',
          defaultMessage: '',
        });
      case 'testVerbum':
        return formatMessage({
          id: 'NAVBAR.TESTS',
          defaultMessage: '',
        });
      case 'practice':
        return formatMessage({
          id: 'NAVBAR.PRACTICE',
          defaultMessage: '',
        });
      case 'vocabulary':
        return formatMessage({
          id: 'NAVBAR.VOCABULARY',
          defaultMessage: '',
        });
      case 'profile':
        return formatMessage({
          id: 'NAVBAR.PROFILE',
          defaultMessage: '',
        });
      case 'store':
        return formatMessage({
          id: 'NAVBAR.PREMIUM',
          defaultMessage: '',
        });
      default:
        return null;
    }
  }

  if (pathname.includes('buy-premium')) {
    return formatMessage({
      id: 'NAVBAR.PREMIUM',
      defaultMessage: '',
    });
  }

  if (pathname.includes('how-to-use')) {
    return formatMessage({
      id: 'NAVBAR.HOW-TO',
      defaultMessage: '',
    });
  }
  if (pathname.includes('login')) {
    return formatMessage({
      id: 'NAVBAR.LOGIN',
      defaultMessage: '',
    });
  }
  if (pathname.includes('signup')) {
    return formatMessage({
      id: 'NAVBAR.SIGNUP',
      defaultMessage: '',
    });
  }
  if (pathname.includes('vision')) {
    return formatMessage({
      id: 'FOOTER.ROUTE.VISION',
      defaultMessage: '',
    });
  }
  if (pathname.includes('privacy')) {
    return formatMessage({
      id: 'FOOTER.ROUTE.PRIVACY',
      defaultMessage: '',
    });
  }
  if (pathname.includes('terms-of-use')) {
    return formatMessage({
      id: 'FOOTER.ROUTE.TERMS',
      defaultMessage: '',
    });
  }

  return null;
};

const Navbar = ({ loggedIn, premiumUser, activeDictionary, username }) => {
  const { formatMessage } = useIntl();
  const { desktop } = useScreen();
  const history = useHistory();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const toggleDrawer = (event) => {
    if (
      event &&
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }

    setDrawerOpen(!drawerOpen);
  };

  const renderMenu = () => (
    <Menu
      anchorEl={anchorEl}
      id="account-menu"
      keepMounted
      open={!!anchorEl}
      onClose={handleMenuClose}
    >
      <MenuItem
        onClick={() => {
          history.push('/app/profile');
          handleMenuClose();
        }}
      >
        {formatMessage({
          id: 'NAVBAR.PROFILE',
        })}
      </MenuItem>
      <MenuItem
        onClick={() => {
          history.push('/logout');
          handleMenuClose();
        }}
      >
        {formatMessage({
          id: 'NAVBAR.LOGOUT',
        })}
      </MenuItem>
    </Menu>
  );

  const renderNavBarItems = () => {
    if (loggedIn) {
      return desktop ? (
        <>
          <Logo inApp />
          <NavItems
            routes={appRoutes}
            desktop={desktop}
            loggedIn={loggedIn}
            isActiveDictionary={activeDictionary}
            inApp
          />
          <StyledPremiumWrapper premiumUser={premiumUser}>
            {!premiumUser && (
              <ButtonWithTooltip
                onClick={() => history.push('/app/store/premium')}
                buttoncolor="white"
                backgroundcolor="yellow"
                buttonVariant="contained"
              >
                {formatMessage({
                  id: 'NAVBAR.PREMIUM',
                })}
              </ButtonWithTooltip>
            )}
            <Avatar
              width="45px"
              height="45px"
              backgroundcolor="white"
              color="darkGray"
              margin="0"
              username={username}
              onClick={handleProfileMenuOpen}
              showCrown={premiumUser}
            />
            {renderMenu()}
          </StyledPremiumWrapper>
        </>
      ) : (
        <>
          <Logo inApp />
          <StyledTypographyMobileHeader
            variant="h5"
            color="textSecondary"
            align="center"
          >
            {renderMobileHeader(location, formatMessage)}
          </StyledTypographyMobileHeader>
          <ButtonWithTooltip
            iconButton
            IconComponent={MenuIcon}
            onClick={toggleDrawer}
            buttoncolor="white"
            buttonVariant="contained"
            buttonsize="medium"
            buttonDisabled={false}
          />
          <SwipeableDrawer
            anchor="right"
            open={drawerOpen}
            onClose={toggleDrawer}
            onOpen={toggleDrawer}
          >
            <StyledAvatarMobileWrapper desktop={desktop}>
              <Avatar
                width="45px"
                height="45px"
                backgroundcolor="white"
                color="darkGray"
                username={username}
                margin="0 0 30px 0"
                onClick={() => {
                  history.push('/app/profile');
                  toggleDrawer();
                }}
                showCrown={premiumUser}
              />
              {!premiumUser && (
                <ButtonWithTooltip
                  onClick={() => {
                    history.push('/app/store/premium');
                    toggleDrawer();
                  }}
                  buttoncolor="white"
                  backgroundcolor="yellow"
                  buttonVariant="contained"
                >
                  {formatMessage({
                    id: 'NAVBAR.PREMIUM',
                  })}
                </ButtonWithTooltip>
              )}
            </StyledAvatarMobileWrapper>
            <NavItems
              routes={appRoutes}
              toggleDrawer={toggleDrawer}
              desktop={desktop}
              loggedIn={loggedIn}
              isActiveDictionary={activeDictionary}
              inApp
              username={username}
            />
          </SwipeableDrawer>
        </>
      );
    }

    return desktop ? (
      <>
        <Logo />
        <NavItems routes={homeRoutes} desktop={desktop} loggedIn={loggedIn} />
      </>
    ) : (
      <>
        <Logo />
        <StyledTypographyMobileHeader
          variant="h5"
          color="textSecondary"
          align="center"
        >
          {renderMobileHeader(location, formatMessage)}
        </StyledTypographyMobileHeader>
        <ButtonWithTooltip
          iconButton
          IconComponent={MenuIcon}
          onClick={toggleDrawer}
          buttoncolor="white"
          buttonVariant="contained"
          buttonsize="medium"
          buttonDisabled={false}
        />
        <SwipeableDrawer
          anchor="right"
          open={drawerOpen}
          onClose={toggleDrawer}
          onOpen={toggleDrawer}
        >
          <NavItems
            routes={homeRoutes}
            toggleDrawer={toggleDrawer}
            desktop={desktop}
            loggedIn={loggedIn}
          />
        </SwipeableDrawer>
      </>
    );
  };

  return (
    <FixedWrapper position="relative" color="secondary">
      <Wrapper>{renderNavBarItems()}</Wrapper>
    </FixedWrapper>
  );
};

const mapStateToProps = ({ firebase, firestore }) => ({
  loggedIn: firebase.auth.uid,
  premiumUser: firebase.profile?.premium,
  activeDictionary: firestore?.ordered?.dictionary?.find(
    (diction) => diction.isActive
  ),
  username: firebase.profile.username,
});

export default connect(mapStateToProps, null)(Navbar);
